import React from 'react';
import { orderBy } from 'lodash';
import type { Theme } from 'venn-ui-kit';
import { ColorUtils } from 'venn-ui-kit';
import type { default as d3Selection } from 'd3-selection';
import { select } from 'd3-selection';
import { extent } from 'd3-array';
import { scaleLinear, scaleTime } from 'd3-scale';
import type { Line } from 'd3-shape';
import { line } from 'd3-shape';
import type { DataPoint } from 'venn-utils';
import { Dates } from 'venn-utils';
import { withTheme } from 'styled-components';
import type { ProxyTypeEnum } from 'venn-api';

const height = 34;
const width = 260;
const BANDS_OPACITY = 0.2;
const rhombSize = 2;

type ChartVerticalLine = {
  date: number;
  lineColor: string;
  legendColor: string;
  legendOffset?: number;
};

type SVGType = d3Selection.Selection<SVGSVGElement, Record<string, never>, null, undefined>;
export type ProxyShortInfo = {
  proxyType: ProxyTypeEnum | undefined;
  proxiedStart: number;
  proxiedEnd: number;
};

interface LineChartProps {
  /**
   * Time series for the proxy
   */
  proxy: DataPoint[] | undefined;
  /**
   * Time series for the investment
   */
  investment: DataPoint[] | undefined;
  /**
   * Time series for the investment
   */
  extrapolation: DataPoint[] | undefined;
  /**
   * The current theme
   */
  theme: Theme;

  proxiesShortInfo: ProxyShortInfo[];

  /**
   * CSS class name
   */
  className?: string;
}

const filterByXRange = (points: DataPoint[], rangeStart: number, rangeEnd: number): DataPoint[] =>
  points.filter((point) => point.x >= rangeStart && point.x <= rangeEnd);

/**
 * Splits the investment time series into segments based on proxy short info.
 * This handles gaps in data and associates segments with their specific proxy types.
 */
export const getProxiedRanges = (proxiedPoints: DataPoint[], proxiesShortInfo: ProxyShortInfo[]) => {
  if (!proxiedPoints.length) {
    return [];
  }

  if (!proxiesShortInfo.length) {
    return [
      {
        proxyType: undefined,
        proxiedStart: proxiedPoints[0]?.x ?? 0,
        proxiedEnd: proxiedPoints.at(-1)?.x ?? 0,
        proxiedPoints,
      },
    ];
  }

  const totalStart = proxiedPoints[0]!.x;
  const totalEnd = proxiedPoints.at(-1)!.x;

  const proxiedRanges = proxiesShortInfo
    .map((proxyShortInfo) => ({
      ...proxyShortInfo,
      proxiedPoints: filterByXRange(proxiedPoints, proxyShortInfo.proxiedStart, proxyShortInfo.proxiedEnd),
    }))
    .filter(({ proxiedPoints }) => proxiedPoints.length);

  const result: typeof proxiedRanges = [];

  proxiedRanges.forEach((current, i) => {
    const gapStart = i === 0 ? totalStart : proxiedRanges[i - 1]!.proxiedEnd;
    const gapEnd = current.proxiedStart;

    if (gapStart < gapEnd) {
      const gapPoints = filterByXRange(proxiedPoints, gapStart, gapEnd);
      if (gapPoints.length) {
        result.push({
          proxyType: undefined,
          proxiedStart: gapStart,
          proxiedEnd: gapEnd,
          proxiedPoints: gapPoints,
        });
      }
    }

    result.push(current);
  });

  // Trailing gap after the last proxied range
  const lastRangeEnd = proxiedRanges.at(-1)?.proxiedEnd ?? totalStart;
  if (lastRangeEnd < totalEnd) {
    const trailingPoints = filterByXRange(proxiedPoints, lastRangeEnd, totalEnd);
    if (trailingPoints.length) {
      result.push({
        proxyType: undefined,
        proxiedStart: lastRangeEnd,
        proxiedEnd: totalEnd,
        proxiedPoints: trailingPoints,
      });
    }
  }

  return result;
};

/**
 * Renders a D3 line chart visualizing proxy, investment, and extrapolation data series.
 * Handles extensive customization for different proxy types (e.g., Substitute, Interpolate)
 * and visualizes time ranges with colored bands and markers.
 */
const LineChart = ({
  proxy,
  investment,
  extrapolation,
  theme: { Colors, Schemes },
  proxiesShortInfo,
  className,
}: LineChartProps) => {
  const nodeRef = React.useRef<SVGSVGElement>(null);

  React.useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) {
      return;
    }
    node.innerHTML = '';

    const investmentColor = Schemes.Proxy.invertedSubjectLine;
    const proxyColor = Schemes.Proxy.proxyLine;
    const extrapolationColor = Schemes.Proxy.extrapolation;

    /* If the investment or proxy data is missing/empty, handle gracefully.
     * Note: Proxy data might be missing if proxyType is EXTRAPOLATION. */
    const hasInvestment = Boolean(investment?.length);
    const hasProxy = Boolean(proxy?.length);
    if (!hasProxy && !hasInvestment) {
      return;
    }

    const isSubstitute = proxiesShortInfo[0]?.proxyType === 'SUBSTITUTE';
    const hasExtrapolation = Boolean(extrapolation?.length);

    const proxyStartDate = hasProxy ? proxy?.[0]?.x : undefined;
    const proxyEndDate = hasProxy ? proxy?.at(-1)?.x : undefined;

    /* Determine the start and end dates for the investment series if available. */
    const investmentStartDate = hasInvestment ? investment?.[0]?.x : undefined;
    const investmentEndDate = hasInvestment ? investment?.at(-1)?.x : undefined;

    /* Calculate extrapolation highlight range.
     * Starts immediately after the latest available data point (investment or proxy) to avoid visual gaps. */
    const extrapolationHighlightStart = hasExtrapolation
      ? Math.max(investmentEndDate ?? 0, proxyEndDate ?? 0)
      : undefined;
    const extrapolationHighlightEnd = hasExtrapolation ? extrapolation?.at(-1)?.x : undefined;

    const start = Math.min(proxyStartDate ?? Infinity, investmentStartDate ?? Infinity);
    const end = Math.max(proxyEndDate ?? 0, investmentEndDate ?? 0, extrapolationHighlightEnd ?? 0);

    const svg: SVGType = select(node);
    const seriesMargin = 3;
    const margins = { top: 0, bottom: 10, right: 0, left: 0 };
    const chartHeight = height - margins.bottom;

    const series = svg.append('g').attr('transform', `translate(${margins.left}, ${margins.top})`);

    const filteredProxiesShortInfo = proxiesShortInfo.filter(({ proxyType }) => proxyType !== 'EXTRAPOLATE');
    const investmentProxiedRanges = getProxiedRanges(investment ?? [], filteredProxiesShortInfo);
    const proxyDatum = proxy?.map((d) => [d.x, d.y]) ?? [];
    const investmentDatum = investment?.map((d) => [d.x, d.y]) ?? [];
    const extrapolationDatum = extrapolation?.map((d) => [d.x, d.y]) ?? [];

    /* Combine all data points to determine the global X and Y domains for the chart scales. */
    const allDatum = [...investmentDatum, ...proxyDatum, ...extrapolationDatum];
    const allDatumOrdered = orderBy(allDatum, (datum) => datum[0]);

    // we can make a type assertion because we're sure `allDatumOrdered` is nonempty
    const xRange = extent(allDatumOrdered, (d) => d[0]) as [number, number];
    const xScale = scaleTime()
      .rangeRound([0, width - margins.left - margins.right])
      .domain(xRange);
    const yScale = scaleLinear()
      .rangeRound([height - margins.top - margins.bottom - seriesMargin * 2, seriesMargin])
      .domain(extent(allDatumOrdered, (d) => d[1]) as [number, number]);

    const seriesLine = line()
      .x((d) => xScale(d[0]))
      .y((d) => yScale(d[1]));

    if (hasProxy) {
      addSeriesLine(series, proxyDatum, proxyColor, seriesLine);

      addRect(
        svg,
        xScale(proxyStartDate!),
        xScale(proxyEndDate!) - xScale(proxyStartDate!),
        chartHeight,
        ColorUtils.hex2rgba(proxyColor, BANDS_OPACITY),
      );
    }

    /** Collect vertical line descriptors; they will be deduplicated and rendered in a single pass at the end. */
    const verticalLines: ChartVerticalLine[] = [];

    /* Render investment-specific elements (bands, lines, separators) if data exists. */
    if (hasInvestment) {
      /* If not a direct substitute, visualize the relationship between proxy and investment ranges. */
      if (!isSubstitute) {
        if (hasProxy) {
          if (proxyStartDate! > investmentStartDate!) {
            addRect(
              svg,
              xScale(investmentStartDate!),
              xScale(proxyStartDate!) - xScale(investmentStartDate!),
              chartHeight,
              ColorUtils.hex2rgba(investmentColor, BANDS_OPACITY),
            );

            verticalLines.push({
              date: proxyStartDate!,
              lineColor: Schemes.Proxy.proxyLine,
              legendColor: Colors.GreyScale.Grey60,
            });
          }

          if (proxyEndDate! < investmentEndDate!) {
            addRect(
              svg,
              xScale(proxyEndDate!),
              xScale(investmentEndDate!) - xScale(proxyEndDate!),
              chartHeight,
              ColorUtils.hex2rgba(investmentColor, BANDS_OPACITY),
            );
          }
        } else {
          addRect(
            svg,
            xScale(investmentStartDate!),
            xScale(investmentEndDate!) - xScale(investmentStartDate!),
            chartHeight,
            ColorUtils.hex2rgba(investmentColor, BANDS_OPACITY),
          );
        }
      }

      investmentProxiedRanges.forEach(({ proxiedPoints, proxyType }, i) => {
        if (proxyType === 'INTERPOLATE') {
          const width = xScale(proxiedPoints.at(-1)!.x) - xScale(proxiedPoints[0]!.x);
          const maxSeriesPoints = width / (rhombSize + 8);
          const seriesPoints = reduceSeriesPoints(proxiedPoints, maxSeriesPoints);

          series
            .selectAll('.rhomb')
            .data(
              seriesPoints.map((d) => ({
                x: xScale(d.x),
                y: yScale(d.y),
              })),
            )
            .enter()
            .append('polygon')
            .attr('class', 'rhomb')
            .attr('points', `${rhombSize},0 ${rhombSize * 2},${rhombSize} ${rhombSize},${rhombSize * 2} 0,${rhombSize}`)
            .attr('fill', 'white')
            .attr('transform', (d) => `translate(${d.x - rhombSize}, ${d.y - rhombSize})`);
        } else {
          const nextRange = investmentProxiedRanges[i + 1];
          const nextPoint = nextRange?.proxiedPoints[0];

          addSeriesLine(
            series,
            [...proxiedPoints, ...(nextPoint ? [nextPoint] : [])].map((d) => [d.x, d.y]),
            investmentColor,
            seriesLine,
          );
        }
      });

      svg
        .append('circle')
        .attr('cx', xScale(investmentStartDate!))
        .attr('cy', yScale(investment![0]!.y))
        .attr('r', 2.5)
        .attr('fill', Schemes.Proxy.invertedSubjectLine);

      svg
        .append('circle')
        .attr('cx', xScale(investmentEndDate!))
        .attr('cy', yScale(investment!.at(-1)!.y))
        .attr('r', 2.5)
        .attr('fill', Schemes.Proxy.invertedSubjectLine);
    }

    if (hasExtrapolation) {
      addSeriesLine(series, extrapolationDatum, extrapolationColor, seriesLine);

      // show visual highlight for extrapolation
      addRect(
        svg,
        xScale(extrapolationHighlightStart!),
        xScale(extrapolationHighlightEnd!) - xScale(extrapolationHighlightStart!),
        chartHeight,
        ColorUtils.hex2rgba(extrapolationColor, BANDS_OPACITY),
      );

      verticalLines.push({
        date: extrapolationHighlightStart!,
        lineColor: Colors.DEPRECATED_DataLineColor.PaleBlue,
        legendColor: Colors.GreyScale.Grey60,
        legendOffset: 16,
      });
    }

    if (hasInvestment) {
      /* Adjust the label position to prevent overlap if the investment start/end dates differ from the global start/end. */
      if (investmentStartDate !== start) {
        verticalLines.push({
          date: investmentStartDate!,
          lineColor: Schemes.Proxy.proxyLine,
          legendColor: Colors.GreyScale.Grey60,
        });
      }

      if (investmentEndDate !== end && investmentEndDate !== extrapolationHighlightStart) {
        verticalLines.push({
          date: investmentEndDate!,
          lineColor: Colors.GreyScale.Grey50,
          legendColor: Colors.GreyScale.Grey60,
          legendOffset: 16,
        });
      }
    }

    // Deduplicate and render all vertical lines with legends in a single pass
    const seenPositions = new Set<number>();
    verticalLines.forEach(({ date, lineColor, legendColor, legendOffset }) => {
      const xPos = xScale(date);
      if (!seenPositions.has(xPos)) {
        seenPositions.add(xPos);
        addVerticalLineWithLegend(svg, xPos, date, chartHeight, lineColor, legendColor, legendOffset);
      }
    });

    // add date legends at the chart boundaries
    addLegend(svg, xScale(start), start, Colors.GreyScale.Grey60);
    addLegend(svg, xScale(end), end, Colors.GreyScale.Grey60, 10);
  }, [proxy, investment, extrapolation, Colors, Schemes, proxiesShortInfo]);

  return <svg className={className} viewBox={`0 0 ${width} ${height}`} ref={nodeRef} />;
};

export const addLegend = (svg: SVGType, x: number, date: number, color: string, offset?: number) => {
  const year = Dates.parse(date).format("'YY");
  svg
    .append('text')
    .attr('x', x - (offset || 0))
    .attr('y', 32)
    .attr('fill', color)
    .attr('font-size', 8)
    .text(year);
};

export const addRect = (svg: SVGType, x: number, width: number, height: number, fill: string) => {
  svg
    .append('g')
    .append('rect')
    .attr('x', x)
    .attr('y', 0)
    .attr('width', width)
    .attr('height', height)
    .attr('fill', fill);
};

export const addVerticalLine = (svg: SVGType, x: number, height: number, color: string) => {
  svg
    .append('line')
    .attr('x1', x)
    .attr('y1', 0)
    .attr('x2', x)
    .attr('y2', height)
    .attr('stroke-dasharray', '4 1')
    .attr('stroke', color);
};

export const reduceSeriesPoints = (series: DataPoint[], maxPoints: number) => {
  if (series.length <= maxPoints) {
    return series;
  }

  return Array.from(
    { length: maxPoints },
    (_, i) => series[Math.round((i * (series.length - 1)) / (maxPoints - 1))],
  ) as DataPoint[];
};

/** Renders a dashed vertical line with a year legend label beneath it. */
export const addVerticalLineWithLegend = (
  svg: SVGType,
  x: number,
  date: number,
  chartHeight: number,
  lineColor: string,
  legendColor: string,
  legendOffset?: number,
) => {
  addVerticalLine(svg, x, chartHeight, lineColor);
  addLegend(svg, x, date, legendColor, legendOffset);
};

/**
 * Appends a D3 line path to the given series group.
 * @param series - The SVG group element to append the line to
 * @param datum - Array of [x, y] coordinate pairs
 * @param color - Stroke color for the line
 * @param seriesLine - D3 line generator function
 */
export const addSeriesLine = (
  series: d3Selection.Selection<SVGGElement, Record<string, never>, null, undefined>,
  datum: number[][],
  color: string,
  seriesLine: Line<[number, number]>,
) => {
  series
    .append('path')
    .datum(datum)
    .attr('fill', 'none')
    .attr('stroke', color)
    .attr('stroke-linejoin', 'round')
    .attr('stroke-linecap', 'round')
    .attr('stroke-width', 1)
    // @ts-expect-error: TODO fix strictFunctionTypes
    .attr('d', seriesLine);
};

export default withTheme(LineChart);
